/**
 * brand-colors.ts
 * Generates a 9-shade CSS custom property scale from a single brand hex color.
 * Used to override --color-primary-* vars inside the authenticated app shell.
 *
 * Contrast law — frame `30b` (design round 30, 2026-07-28):
 *   1. Link colour derives PER THEME, moving lightness AWAY from the surface
 *      until 4.5:1. Hue and saturation are never touched, so the link still
 *      reads as the brand. Both values are stored (`--brand-link-on-light` /
 *      `--brand-link-on-dark`) and the theme class picks one in CSS, so the
 *      theme toggle re-resolves them with no JS re-run.
 *   2. `--border-focus` (and the focus ring) derive separately at a 3:1 floor —
 *      a focus indicator is a graphical object, not text.
 *   3. `--btn-primary-bg` keeps the customer's byte-exact hex. Never derived,
 *      never darkened: `getContrastText()` cannot fail (white clears 4.5:1
 *      below relative luminance 0.1833, black above 0.175 — the intervals
 *      overlap, so the failure band is empty), which is why the old plan to
 *      "darken the brand until the label clears AA" was retracted.
 *   4. Escape hatch: if the compliant link must move more than 20 lightness
 *      points from the brand hex, the link stops being the brand colour —
 *      neutral text with a brand underline. `#FFD400`'s compliant link is
 *      olive, and no customer accepts olive as their yellow.
 *   5. The 9-shade clamp works in BOTH directions, so a dark brand still gets
 *      nine distinguishable shades instead of four identical ones.
 *
 * The defect this replaces was `--text-link = lightness − 10`, which moved
 * toward dark whichever way the surface lay and failed AA on three of four
 * reference palettes on the dark app surface.
 */
import { contrastRatio } from './contrast'
import { APP_BRAND_PALETTE, APP_BRAND_VARS, FONT_STACKS } from './design-tokens.generated'

function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h = 0, s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }
  return [h * 360, s * 100, l * 100]
}

function hslToHex(h: number, s: number, l: number): string {
  h /= 360; s /= 100; l /= 100
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  let r: number, g: number, b: number
  if (s === 0) {
    r = g = b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }
  return '#' + [r, g, b].map((x) => Math.round(x * 255).toString(16).padStart(2, '0')).join('')
}

export interface BrandVarInput {
  primaryHex: string | null | undefined
  secondaryHex?: string | null
  tertiaryHex?: string | null
  neutralHex?: string | null
  fontFamily?: string | null
}

/**
 * Resolve a brand `font_family` value to a full CSS font stack.
 * The brand font name is listed FIRST so that if/when its webfont is loaded
 * (e.g. an Adobe Fonts kit for "Bree"), it activates automatically. A
 * visually-close loaded fallback follows, then the app defaults.
 */
const BRAND_FONT_FALLBACK: Record<string, string> = {
  // Nortura Proff uses Bree — now self-hosted as a real @font-face (see
  // index.css). No interim fallback font needed; generic sans follows below.
}

/**
 * The app's OWN typography, as literals, so a surface can re-declare it.
 *
 * Generated from tokens.css by `scripts/gen-tokens.mjs` (22.10.2026; it used to be a
 * hand-kept copy of index.css and went stale the day the app face changed). Re-exported
 * here because the app shell has to be able to state its own identity rather than
 * inherit whatever the document currently says, and it cannot read `:root`
 * for that once `:root` has already been overwritten.
 */
export const APP_FONT_STACKS = FONT_STACKS

export function brandFontStack(fontFamily: string | null | undefined): string | null {
  const name = (fontFamily ?? '').trim()
  if (!name || name.toLowerCase() === 'inter') return null // app default
  // Serif brands must fall back to SERIF, not the app sans — same heuristic as
  // resolveDisplayFont in lib/event-theme.ts (kept in both resolvers on purpose:
  // importing it here would create an import cycle through theme-generator).
  // Before this, a serif tenant font degraded to Inter while the event path
  // degraded correctly to Georgia — the two resolvers disagreed.
  const serifish = /garamond|playfair|baskerville|fraunces|spectral|cormorant|serif|times|georgia/i.test(name)
  if (serifish) return `'${name}', Georgia, 'Times New Roman', serif`
  const fallback = BRAND_FONT_FALLBACK[name.toLowerCase()]
  const parts = [`'${name}'`]
  if (fallback) parts.push(fallback)
  parts.push("'Geist'", 'ui-sans-serif', 'system-ui', 'sans-serif')
  return parts.join(', ')
}

/**
 * Compute WCAG-compliant contrast text for a given background hex.
 * Prefer white when it clears AA; otherwise use black. This keeps brand
 * buttons such as Nortura's pink consistent with their official treatment.
 *
 * This cannot fail AA (30b): white clears 4.5:1 for every fill with relative
 * luminance ≤ 0.1833 and black for every fill ≥ 0.175, and those intervals
 * overlap. The label on a brand button is therefore always readable, whatever
 * hex the customer picks — which is why the fill keeps their exact hex.
 */
function getContrastText(bgHex: string): string {
  return contrastRatio(bgHex, '#FFFFFF') >= 4.5 ? '#FFFFFF' : '#000000'
}

/**
 * The worst-case DS2 surface in each theme, read from `index.css`:
 *   light — `--surface-muted` #EEF1F5 is the DARKEST light surface
 *   dark  — `--surface-elevated` #262C37 is the LIGHTEST dark surface, and it
 *           is lighter than every `--surface-public*` value (#090B11 …
 *           #1E1B4B), so clearing it clears the public surfaces too.
 * Deriving against the worst case means the token is readable on all four
 * surfaces of its theme, not only on `--surface-page`.
 */
const WORST_CASE_SURFACE = { light: '#EEF1F5', dark: '#262C37' } as const

export type ThemeSide = keyof typeof WORST_CASE_SURFACE

/** 30b·4 — past this many lightness points the link is no longer the brand. */
const LINK_DRIFT_LIMIT = 20

/** The app's dark page surface (index.css `.dark --surface-page`), the base
 *  the dark-side brand TINTS are mixed against. */
const DARK_PAGE = '#0E1116'

/** Linear blend between two hex colors (t=0 → a, t=1 → b). */
function mixHex(a: string, b: string, t: number): string {
  const ah = a.replace('#', ''), bh = b.replace('#', '')
  const c = (i: number) => {
    const x = parseInt(ah.slice(i, i + 2), 16)
    const y = parseInt(bh.slice(i, i + 2), 16)
    return Math.round(x + (y - x) * t).toString(16).padStart(2, '0')
  }
  return `#${c(0)}${c(2)}${c(4)}`
}

/**
 * Move a colour's lightness AWAY from the surface until it clears `target`,
 * keeping hue and saturation. Returns the derived hex and how far it travelled.
 * 30b·1: `− 10` was wrong because it moved toward dark regardless of which way
 * the surface lay; direction has to come from the surface, not from a constant.
 */
function deriveAwayFromSurface(
  brandHex: string,
  surfaceHex: string | readonly string[],
  target: number,
): { hex: string; deltaL: number } {
  const surfaces = typeof surfaceHex === 'string' ? [surfaceHex] : surfaceHex
  const minimum = (ink: string) => Math.min(...surfaces.map(surface => contrastRatio(ink, surface)))
  if (minimum(brandHex) >= target) return { hex: brandHex, deltaL: 0 }
  const [h, s, l] = hexToHsl(brandHex)
  // "Away" is decided by the surface: lighten on a dark surface, darken on a
  // light one — whichever pole the surface itself contrasts better against.
  const lighten = minimum('#FFFFFF') > minimum('#000000')
  for (let step = 1; step <= 100; step++) {
    const nextL = lighten ? l + step : l - step
    if (nextL < 0 || nextL > 100) break
    const candidate = hslToHex(h, s, nextL)
    if (minimum(candidate) >= target) return { hex: candidate, deltaL: step }
  }
  // Only reachable when the surface itself cannot support the target; fall back
  // to the max-contrast pole rather than shipping something unreadable.
  return { hex: lighten ? '#FFFFFF' : '#000000', deltaL: 100 }
}

export interface DerivedLink {
  /** The colour to use for link text — a hex, or a var() chain when hatched. */
  color: string
  /** text-decoration-color: the brand hex when hatched, else `currentColor`. */
  underline: string
  /** text-decoration-line at rest: `underline` when hatched, else `none`. */
  decoration: 'underline' | 'none'
  /** True when 30b·4 fired and the link is no longer the brand colour. */
  hatched: boolean
}

/**
 * 30b·1 + 30b·4 — the link colour for one brand hex on one surface.
 * When the compliant colour drifts more than 20 lightness points the link stops
 * being the brand colour and becomes neutral text with a brand underline, which
 * keeps BOTH the readability and the brand association instead of trading one
 * for the other.
 */
export function deriveLinkColor(
  brandHex: string,
  surfaceHex: string | readonly string[],
  side: 'light' | 'dark' = 'light',
): DerivedLink {
  const { hex, deltaL } = deriveAwayFromSurface(brandHex, surfaceHex, 4.5)
  if (deltaL > LINK_DRIFT_LIMIT) {
    // 30b·4, revised 30.10.2026 (Nyx decision, WCAG 1.4.11): the underline is the only mark
    // that carries the brand, so it must be SEEN. Light keeps the exact hex; dark derives it
    // from the brand's hue to 3:1 against the surface. Byte-exact drew #122D24 on the #1C1620
    // card, an underline nobody could see.
    const underline = side === 'dark' ? deriveFocusColor(brandHex, surfaceHex) : brandHex
    return { color: 'var(--text-primary)', underline, decoration: 'underline', hatched: true }
  }
  // Task 40: `underline`, not `none`. This branch is the COMPLIANT brand — the
  // colour cleared contrast, so the link keeps it — and it used to return
  // `none` because `none` was the app's own default at the time. That default
  // is now `underline`, and a brand that sets the property EXPLICITLY never
  // reaches the fallback in `index.css`, so leaving it here would have meant
  // every white-label surface kept colour-only links while the unbranded app
  // was fixed. Measured on `/event/public/:token`, where the token resolved to
  // `none` after the index.css change and gave this away.
  return { color: hex, underline: 'currentColor', decoration: 'underline', hatched: false }
}

/**
 * 30b·2 — focus indicators are graphical objects (WCAG 1.4.11), so they derive
 * at a 3:1 floor and never take the escape hatch: a focus ring that is not the
 * brand colour is still a focus ring, but one that is invisible is a defect.
 */
export function deriveFocusColor(brandHex: string, surfaceHex: string | readonly string[]): string {
  return deriveAwayFromSurface(brandHex, surfaceHex, 3).hex
}

/**
 * Ink for a SUBTLE BRAND TINT — text painted on a surface that is itself made
 * of the brand. Derives against that tint, never against the page.
 *
 * No escape hatch here, and that is the difference from `deriveLinkColor`. A
 * link that drifts stops reading as the brand, so it hands the colour back and
 * keeps a brand underline. This ink sits ON a surface the brand already owns,
 * so the tint carries the association and the ink only has to be legible: it
 * may travel as far as it needs to.
 *
 * Why it exists at all: the app used ramp rungs as roles, pairing
 * `--color-primary-700` on `--color-primary-50`. Under a dark generated theme
 * the generator remaps the tint rungs dark but leaves the ink rungs dark too,
 * so both ends move the same way and the pair collapses — measured 1.71:1 on
 * the live Nortura theme. The rung cannot be repaired in place because
 * `--btn-primary-bg-active` is the same rung used as a FILL under white text.
 */
export function deriveInkOnTint(brandHex: string, tintHex: string): string {
  return deriveAwayFromSurface(brandHex, tintHex, 4.5).hex
}

/**
 * Generate a 9-shade CSS var object for a given brand hex color.
 * Returns an object like { '--color-primary-50': '#...', ... '--color-primary-900': '#...' }
 * --btn-primary-bg and --action-primary-bg use the exact brand hex (primary-500),
 * not a darker shade.
 */
export function isNativeBrandPalette(primaryHex: string, accentHex?: string | null): boolean {
  return primaryHex.toLowerCase() === APP_BRAND_PALETTE.primary.toLowerCase() &&
    accentHex?.toLowerCase() === APP_BRAND_PALETTE.secondary.toLowerCase()
}

export function generateBrandVars(primaryHex: string, opts: { accentHex?: string | null } = {}): Record<string, string> {
  if (!primaryHex || !/^#[0-9a-fA-F]{6}$/.test(primaryHex)) return {}
  // Only the complete reserved tuple denotes the native preset. A custom brand
  // sharing either color alone still uses the generic, byte-exact generator.
  if (isNativeBrandPalette(primaryHex, opts.accentHex)) {
    return { ...APP_BRAND_VARS }
  }
  const [h, s, l] = hexToHsl(primaryHex)
  // 30b·5 — the darker stops must stay distinguishable for a DARK brand.
  // `Math.max(l - n, floor)` clipped every stop onto the same floor once the
  // brand was dark: #0F5132 yielded six distinct shades out of nine, and the
  // Nortura tenant's #111111 collapsed 600–900 onto near-black. Compress the
  // four deltas into whatever room exists below the brand instead of clipping.
  // A brand light enough to have room (l ≥ 43) gets exactly the old values.
  const DARK_FLOOR = 3
  const room = Math.max(l - DARK_FLOOR, 0)
  const k = room >= 40 ? 1 : room / 40
  const darker = (delta: number) => Math.max(l - delta * k, DARK_FLOOR)
  // Fixed lightness stops — 500 is always the brand color
  const stops: Array<[string, number]> = [
    ['50',  97],
    ['100', 94],
    ['200', 88],
    ['300', 78],
    ['400', 64],
    ['500', l],
    ['600', darker(10)],
    ['700', darker(20)],
    ['800', darker(30)],
    ['900', darker(40)],
  ]
  const vars: Record<string, string> = {}
  for (const [shade, lightness] of stops) {
    vars[`--color-primary-${shade}`] = hslToHex(h, s, lightness)
  }
  // Button and action vars: primary-500 is the exact brand hex passed in.
  // Hover/active must be PERCEIVABLE: a near-black brand has nothing left below
  // it, so those states lighten instead of darkening into invisibility.
  const canDarken  = l >= 20
  let hoverHex  = canDarken ? hslToHex(h, s, darker(8))  : hslToHex(h, s, Math.min(l + 10, 100))
  let activeHex = canDarken ? hslToHex(h, s, darker(16)) : hslToHex(h, s, Math.min(l + 18, 100))
  const textColor = getContrastText(primaryHex) // WCAG-computed: white or black
  // Keep one ink throughout the interaction. If either state crosses its AA
  // floor, move BOTH states away from the ink, preserving a coherent direction
  // from rest → hover → press. The customer's resting fill stays byte-exact.
  if (contrastRatio(textColor, hoverHex) < 4.5 || contrastRatio(textColor, activeHex) < 4.5) {
    const direction = textColor === '#FFFFFF' ? -1 : 1
    hoverHex = hslToHex(h, s, Math.max(0, Math.min(l + direction * 8, 100)))
    activeHex = hslToHex(h, s, Math.max(0, Math.min(l + direction * 16, 100)))
  }
  vars['--btn-primary-bg']          = primaryHex  // 30b·3 — byte-exact, never derived
  vars['--btn-primary-bg-hover']    = hoverHex
  vars['--btn-primary-bg-active']   = activeHex
  vars['--btn-primary-text']        = textColor   // ensures readable text on any brand color
  vars['--action-primary-bg']       = primaryHex  // 30b·3 — byte-exact, never derived
  vars['--action-primary-bg-hover'] = hoverHex
  vars['--action-primary-text']     = textColor

  // 30b·1 + 30b·2 — both themes are derived and BOTH are stored. `index.css`
  // maps them onto --text-link / --border-focus / --focus-ring-color per theme
  // class, so switching theme re-resolves them without re-running this code.
  for (const side of ['light', 'dark'] as ThemeSide[]) {
    const surface = WORST_CASE_SURFACE[side]
    const link = deriveLinkColor(primaryHex, surface, side)
    vars[`--brand-link-on-${side}`] = link.color
    vars[`--brand-link-underline-on-${side}`] = link.underline
    vars[`--brand-link-decoration-on-${side}`] = link.decoration
    vars[`--brand-focus-on-${side}`] = deriveFocusColor(primaryHex, surface)
  }

  // Light-tint shades (50/100/200), per theme, same both-sides mechanism.
  // The fixed near-white stops above are correct ONLY on a light surface;
  // components use them as subtle selected/hover fills under `text-primary`,
  // which flips near-white in dark mode → white-on-white. The event-theme path
  // fixed this in its own generator (62d6e08); the tenant path ships the same
  // page-mixed dark tints here, and `index.css`'s [data-brand-scope] rules pick
  // the side from the theme class. `applyBrandVars` deliberately does NOT set
  // --color-primary-50/100/200 inline any more — inline values on the scope
  // element would beat those CSS rules and reintroduce the bug.
  vars['--brand-primary-50-on-light'] = vars['--color-primary-50']
  vars['--brand-primary-100-on-light'] = vars['--color-primary-100']
  vars['--brand-primary-200-on-light'] = vars['--color-primary-200']
  vars['--brand-primary-50-on-dark'] = mixHex(DARK_PAGE, primaryHex, 0.18)
  vars['--brand-primary-100-on-dark'] = mixHex(DARK_PAGE, primaryHex, 0.28)
  vars['--brand-primary-200-on-dark'] = mixHex(DARK_PAGE, primaryHex, 0.42)

  // The filled action per theme (23.10.2026). The light side is the brand, byte-exact. The dark
  // side is the brand too, unless the brand IS a dark surface: PROFF's #111111 drew black buttons
  // on the navy shell, 1.1:1, a button you could not see. Then the tenant's own accent carries it
  // (PROFF: magenta), the same answer the public page had reached alone in usePublicEventBrand.
  const light = { bg: primaryHex, hover: hoverHex, active: activeHex, text: textColor }
  const dark = darkActionFamily(primaryHex, opts.accentHex ?? null) ?? light
  for (const [side, f] of [['light', light], ['dark', dark]] as const) {
    vars[`--brand-action-bg-on-${side}`] = f.bg
    vars[`--brand-action-bg-hover-on-${side}`] = f.hover
    vars[`--brand-action-bg-active-on-${side}`] = f.active
    vars[`--brand-action-text-on-${side}`] = f.text
  }

  // The subtle callout pair per theme, same both-sides mechanism as the tints. `index.css`
  // re-declares --surface-brand-subtle / --text-on-brand-subtle from these inside
  // [data-brand-scope]; without them a tenant's selected tiles stayed EventRipple tangerine
  // under its green buttons (triple assessment 30.10.2026). The ink is derived against the
  // exact tint, so the pair can never drift apart.
  for (const side of ['light', 'dark'] as ThemeSide[]) {
    const tint = vars[`--brand-primary-50-on-${side}`]
    vars[`--brand-subtle-bg-on-${side}`] = tint
    vars[`--brand-subtle-ink-on-${side}`] = deriveInkOnTint(primaryHex, tint)
  }
  return vars
}

/** The dark theme's surfaces a filled button must stand out from (index.css `.dark`): the page,
 *  and the lightest raised surface. 3:1 against both is WCAG 1.4.11 for a control's boundary. */
const DARK_ACTION_SURFACES = ['#141017', '#2A2230'] as const
const visibleOnDark = (hex: string) => DARK_ACTION_SURFACES.every((surface) => contrastRatio(hex, surface) >= 3)

interface ActionFamily { bg: string; hover: string; active: string; text: string }

/**
 * The dark-theme action family, or null when the brand fill already stands out on dark.
 *
 * Order: the tenant's accent when it is visible on dark, else the brand itself lifted in
 * lightness until it is (hue kept), else a near-white fill for a colourless brand, where a lift
 * would only produce a grey that reads as disabled. Ink: white when white passes 4.5:1 (a
 * brand's own buttons carry white far more often than black; PROFF's magenta is 4.50 white,
 * 4.67 black), else the generator's choice. Hover and press move AWAY from the ink, so the
 * label never drops under 4.5:1 mid-interaction.
 */
function darkActionFamily(primaryHex: string, accentHex: string | null): ActionFamily | null {
  if (visibleOnDark(primaryHex)) return null
  const accent = accentHex && /^#[0-9a-fA-F]{6}$/.test(accentHex.trim()) ? accentHex.trim() : null
  let bg: string
  if (accent && visibleOnDark(accent)) bg = accent
  else {
    const [h, s, l] = hexToHsl(primaryHex)
    if (s < 12) bg = hslToHex(h, s, 94)
    else {
      let next = l
      bg = primaryHex
      while (next < 90 && !visibleOnDark(bg)) bg = hslToHex(h, s, (next += 4))
    }
  }
  const text = contrastRatio('#FFFFFF', bg) >= 4.5 ? '#FFFFFF' : getContrastText(bg)
  const [h, s, l] = hexToHsl(bg)
  const away = text === '#FFFFFF' ? -1 : 1
  const step = (n: number) => hslToHex(h, s, Math.max(0, Math.min(100, l + away * n)))
  return { bg, hover: step(6), active: step(12), text }
}

/** Tint shades resolved per theme via [data-brand-scope] CSS, not inline —
 *  see the comment in generateBrandVars. Skipped by applyBrandVars. */
const CSS_RESOLVED_TINT_KEYS = new Set([
  '--color-primary-50', '--color-primary-100', '--color-primary-200',
  // The filled action resolves per theme too (23.10.2026): index.css maps
  // --btn-primary-* / --action-primary-* from --brand-action-*-on-light/dark.
  '--btn-primary-bg', '--btn-primary-bg-hover', '--btn-primary-bg-active', '--btn-primary-text',
  '--action-primary-bg', '--action-primary-bg-hover', '--action-primary-text',
])

const BRAND_VAR_KEYS = [
  '--color-primary-50', '--color-primary-100', '--color-primary-200',
  '--color-primary-300', '--color-primary-400', '--color-primary-500',
  '--color-primary-600', '--color-primary-700', '--color-primary-800',
  '--color-primary-900',
  '--btn-primary-bg', '--btn-primary-bg-hover', '--btn-primary-bg-active',
  '--btn-primary-text', '--action-primary-text',
  '--action-primary-bg', '--action-primary-bg-hover',
  '--brand-link-on-light', '--brand-link-on-dark',
  '--brand-link-underline-on-light', '--brand-link-underline-on-dark',
  '--brand-link-decoration-on-light', '--brand-link-decoration-on-dark',
  '--brand-focus-on-light', '--brand-focus-on-dark',
  ...Object.keys(APP_BRAND_VARS).filter(key => key.startsWith('--brand-primary-')),
  '--brand-action-bg-on-light', '--brand-action-bg-on-dark',
  '--brand-action-bg-hover-on-light', '--brand-action-bg-hover-on-dark',
  '--brand-action-bg-active-on-light', '--brand-action-bg-active-on-dark',
  '--brand-action-text-on-light', '--brand-action-text-on-dark',
  '--brand-subtle-bg-on-light', '--brand-subtle-bg-on-dark',
  '--brand-subtle-ink-on-light', '--brand-subtle-ink-on-dark',
  // Legacy single-value keys (pre-30b). No longer written, still cleared so a
  // stale inline value from an older bundle cannot outlive a brand reset.
  '--border-focus', '--text-link',
  '--color-secondary', '--color-tertiary', '--color-neutral',
  '--font-sans', '--font-display', '--font-hero',
]

/**
 * Marks the element whose subtree resolves brand tokens. `index.css` hangs the
 * per-theme link/focus mapping off this attribute (30b·1) — it must be present
 * whenever the brand vars are, and gone when they are cleared.
 */
export const BRAND_SCOPE_ATTR = 'data-brand-scope'

/** Apply brand vars as inline styles to a container element ref */
export function applyBrandVars(el: HTMLElement, input: BrandVarInput | null | undefined): void {
  // Support legacy call: applyBrandVars(el, null) for cleanup
  if (!input || !input.primaryHex) {
    for (const key of BRAND_VAR_KEYS) el.style.removeProperty(key)
    el.removeAttribute(BRAND_SCOPE_ATTR)
    return
  }

  // Replacing a brand must also remove values the next brand does not emit,
  // notably Bree when the next choice uses the app face. Re-state the app
  // stacks after clearing so AppLayout's typography pin remains intact.
  for (const key of BRAND_VAR_KEYS) el.style.removeProperty(key)
  for (const [key, value] of Object.entries(APP_FONT_STACKS)) el.style.setProperty(key, value)

  // `secondaryHex` / `tertiaryHex` / `neutralHex` stay on the INPUT type and are
  // deliberately not destructured: callers pass the tenant's whole palette, and
  // narrowing the type would make re-adding a consumer a change at every call
  // site rather than a change here. See the note further down on why nothing is
  // emitted for them.
  const { primaryHex, secondaryHex, fontFamily } = input

  const vars = generateBrandVars(primaryHex, { accentHex: secondaryHex })

  // Brand typography: override the app font stack when the brand specifies one.
  // The editorial hero (--font-hero, Fraunces by default) must inherit the brand
  // font too, so a white-label event's title stays on-brand instead of the app's
  // own serif display face.
  // Headings only (19.10.2026). The brand face is the tenant's VOICE: titles and section heads,
  // which read --font-display / --font-hero (docs/THEMING.md, «Hvor tenantens font gjelder»).
  // Writing --font-sans as well put Bree on 13px body text, buttons, dates and the footer of
  // PROFF's invitation page, below the 16px floor that rule sets. Body text keeps the app face.
  const fontStack = brandFontStack(fontFamily)
  if (fontStack) {
    vars['--font-display'] = fontStack
    vars['--font-hero'] = fontStack
  }

  // Secondary / tertiary / neutral are NOT emitted, and that is the whole
  // statement about them.
  //
  // `BRAND_VAR_NAMES` above has described these three as «legacy single-value
  // keys, no longer written» since 30b. They were still written, forty lines
  // below the comment saying they were not — so the file disagreed with itself
  // and the disagreement read as a feature. Measured 13.10.2026:
  // `var(--color-secondary)`, `var(--color-tertiary)` and `var(--color-neutral)`
  // have ZERO references across the app and DS2, so three CSS variables were
  // being written onto the shell on every brand apply for nothing to read.
  //
  // They stay in `BRAND_VAR_NAMES` deliberately: the cleanup list is what stops
  // a value written by an older bundle outliving a brand reset, and that job is
  // unchanged by no longer writing them.
  //
  // The COLUMNS and the Branding tab's pickers stay too. They are not dead —
  // they carry the tenant's four-colour palette identity, which the brand
  // presets and the preview render as swatches. What was dead is the belief,
  // invited by these three lines, that setting a tertiary colour paints
  // anything. Re-add a var and its consumer together, or neither.

  const nativePalette = isNativeBrandPalette(primaryHex, secondaryHex)
  for (const [key, val] of Object.entries(vars)) {
    if (CSS_RESOLVED_TINT_KEYS.has(key) || (nativePalette && /^--color-primary-\d+$/.test(key))) continue // resolved per theme in CSS
    el.style.setProperty(key, val)
  }
  el.setAttribute(BRAND_SCOPE_ATTR, '')
}
